import { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Link,
  Stack,
  Typography,
} from "@mui/material";

import { IDENTITY_TERMS, TermsItem, allRequiredAgreed } from "./terms";

/**
 * 본인인증 앞에 받는 동의.
 *
 * ## 왜 발송 버튼 **위**에 있나
 *
 * 인증번호 발송은 **문자 한 통이 나가고 과금되는** 되돌릴 수 없는 행위다. 동의를
 * 그 아래에 두면 누른 뒤에 «동의하세요» 를 만나고, 그때는 이미 늦다. 그래서 순서를
 * 화면 순서와 맞춘다 — 읽고 → 동의하고 → 보낸다.
 *
 * ## 왜 전문을 모달로 띄우나
 *
 * 새 탭으로 보내면 **입력하던 주민번호·휴대폰번호가 날아간다.** 돌아와서 다시 치게
 * 되고, 그러면 아무도 안 읽고 «전체 동의» 만 누른다. 읽는 비용을 낮추는 것이
 * 동의를 실제로 받는 유일한 방법이다.
 *
 * 두 주소 모두 `X-Frame-Options`·`frame-ancestors` 가 없어 iframe 으로 뜬다(실측).
 * 나중에 한쪽이 막히면 **모달이 빈 채로 뜨므로**, 그때는 `Link` 로 내려가도록
 * `onError` 가 아니라 아래 «새 창으로 보기» 를 같이 둔다.
 */
type Props = {
  agreed: Record<string, boolean>;
  onChange: (next: Record<string, boolean>) => void;
  disabled?: boolean;
};

export default function TermsConsent({ agreed, onChange, disabled }: Props) {
  const [viewing, setViewing] = useState<TermsItem | null>(null);
  /**
   * 전문이 떴는가.
   *
   * 약관 페이지는 **SPA 라 번들을 받고 나서야** 글이 나온다 — 실측으로 3초 넘게
   * 흰 화면이었다. 표시가 없으면 «고장났다» 로 읽고 닫아 버리고, 그러면 읽지
   * 않은 채 동의하게 된다.
   */
  const [loaded, setLoaded] = useState(false);

  const open = (item: TermsItem) => {
    setLoaded(false);
    setViewing(item);
  };

  const allOn = IDENTITY_TERMS.every((item) => agreed[item.key]);

  /** 전체 동의는 **상태가 아니라 동작**이다. 별도로 저장하면 개별 항목과 어긋난다. */
  const toggleAll = (on: boolean) =>
    onChange(Object.fromEntries(IDENTITY_TERMS.map((item) => [item.key, on])));

  return (
    <Box>
      <Box
        sx={{
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          px: 2,
          py: 1,
          // 로그인 폼이 가운데 정렬이라 그대로 두면 체크박스 줄만 가운데로 밀리고
          // 아래 설명은 왼쪽에 남아 줄이 어긋난다. 동의문은 읽는 글이라 왼쪽이다.
          textAlign: "left",
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={allOn}
              onChange={(event) => toggleAll(event.target.checked)}
              disabled={disabled}
            />
          }
          label={
            <Typography variant="subtitle2">본인확인 약관에 모두 동의합니다</Typography>
          }
        />
        <Divider />
        <Stack sx={{ mt: 0.5 }}>
          {IDENTITY_TERMS.map((item) => (
            <Stack
              key={item.key}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
            >
              <FormControlLabel
                sx={{ mr: 0, flex: 1 }}
                control={
                  <Checkbox
                    size="small"
                    checked={Boolean(agreed[item.key])}
                    onChange={(event) =>
                      onChange({ ...agreed, [item.key]: event.target.checked })
                    }
                    disabled={disabled}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">
                      <Box component="span" sx={{ color: "error.main", mr: 0.5 }}>
                        {item.required ? "[필수]" : "[선택]"}
                      </Box>
                      {item.title}
                    </Typography>
                    {/* 제목만 있으면 무엇에 동의하는지 모른 채 누른다. 한 줄이라도
                        보이면 «내 번호가 어디로 가는가» 에 답이 된다. */}
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      {item.summary}
                    </Typography>
                  </Box>
                }
              />
              <Button
                size="small"
                onClick={() => open(item)}
                sx={{ flexShrink: 0, minWidth: 0 }}
              >
                보기
              </Button>
            </Stack>
          ))}
        </Stack>
      </Box>

      <Dialog
        open={viewing !== null}
        onClose={() => setViewing(null)}
        fullWidth
        maxWidth={viewing?.narrow ? "xs" : "md"}
      >
        <DialogTitle sx={{ pb: 1 }}>{viewing?.title}</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {/* 우리 문서는 바로 그린다 — iframe 이 아니므로 흰 화면도 로딩도 없다. */}
          {viewing?.body && (
            <Box sx={{ maxHeight: "60vh", overflowY: "auto" }}>
              <viewing.body />
            </Box>
          )}
          {viewing?.url && (
            <Box sx={{ position: "relative", height: "60vh" }}>
              <Box
                component="iframe"
                src={viewing.url}
                title={viewing.title}
                onLoad={() => setLoaded(true)}
                sx={{ display: "block", width: "100%", height: "100%", border: 0 }}
              />
              {!loaded && (
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  spacing={1.5}
                  sx={{
                    position: "absolute",
                    inset: 0,
                    bgcolor: "background.paper",
                  }}
                >
                  <CircularProgress size={28} />
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    약관 전문을 불러오고 있습니다…
                  </Typography>
                </Stack>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between" }}>
          {/* 남의 문서일 때만 탈출구를 둔다 — 상대가 나중에 iframe 을 막으면
              모달이 빈 채로 뜨는데, 그때 읽을 길이 하나는 남아 있어야 한다.
              우리 문서는 이미 여기 그려져 있으므로 나갈 곳이 없다. */}
          <Box sx={{ ml: 1 }}>
            {viewing?.url && (
              <Link href={viewing.url} target="_blank" rel="noreferrer" variant="caption">
                새 창으로 보기
              </Link>
            )}
          </Box>
          <Box>
            <Button onClick={() => setViewing(null)}>닫기</Button>
            <Button
              variant="contained"
              onClick={() => {
                if (viewing) onChange({ ...agreed, [viewing.key]: true });
                setViewing(null);
              }}
            >
              동의합니다
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export { allRequiredAgreed };
